import React, { useEffect, FC } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import * as Types from 'types';

import { EditionsStats } from 'editions/components/EditionsStats';
import { BodyBackgroundImage } from 'components/BodyBackgroundImage';
import { EmptyVault } from 'vault/components/EmptyVault';
import { GiftEditionContainer } from 'editions/GiftEditionContainer';

import { jumpToAnchor } from 'utils/jumpToAnchor';

import { withPlayerDispatcher, withPlayerDispatcherProps } from 'player/withPlayerDispatcher';

import { fetchEditionsPlaylist } from 'features/playlists/actions';
import { Edition } from 'components/editions/Edition';
import { EditionDetailsContainer } from 'editions/EditionDetailsContainer';
import { getFirstNewEdition } from 'features/editions/helpers';
import { selectEditionsPlaylist } from 'features/playlists/selectors';
import { Center } from 'components/utils/Center';
import { Spinner } from 'components/Spinner';
import { useQueryParam } from 'hooks/useQueryParam';
import { stringify } from 'query-string';
import { WagmiProvider } from 'context/WagmiContext';

type StateProps = {
  playlist?: Types.Playlist;
  isFetching: boolean;
  editions: Types.Edition[];
  isFirstFetchDone: boolean;
  currentUser: Types.LoggedInUser;
  backgroundUrl?: string;
};

type DispatchProps = {
  fetchEditionsPlaylist: () => void;
};

type HOCProps = withPlayerDispatcherProps & RouteComponentProps<{ id: string }>;

type Props = StateProps & DispatchProps & HOCProps;
export type EditionStep = '1' | '2';

const EditionsGrid: FC<Props> = ({
  isFirstFetchDone,
  backgroundUrl,
  playlist,
  isFetching,
  ...props
}) => {
  useEffect(() => {
    props.fetchEditionsPlaylist();
    jumpToAnchor();
  }, []);

  const step = useQueryParam<EditionStep>('step', '1');

  if (!isFirstFetchDone || isFetching) {
    return (
      <Center>
        <Spinner light />
      </Center>
    );
  }

  return (
    <WagmiProvider>
      <div className='flex-grow-1'>
        {props.editions.length > 9 && (
          <EditionsStats editions={props.editions} user={props.currentUser} />
        )}
        {playlist && playlist?.items?.length > 0 ? (
          <div className='row mb-4'>
            {playlist.items.map((playlistItem) => {
              const edition = playlistItem.edition;

              if (!edition) {
                return null;
              }

              return (
                <Edition
                  className='col-12 col-sm-6 col-md-4 col-lg-3 mb-2'
                  key={playlistItem.id}
                  artwork={playlistItem.artwork}
                  edition={edition}
                  onPlay={() => props.play(playlist.id, String(playlistItem.id))}
                  editionInfoStyle={{
                    color: '#99999C',
                  }}
                  setStep={(step: number) => {
                    const search = stringify({ step });
                    playlistItem.editionId &&
                      props.history.push(
                        `${Routes.vault_edition_path(playlistItem.editionId)}?${search}`,
                      );
                  }}
                />
              );
            })}
          </div>
        ) : (
          <EmptyVault artStreamActive={props.currentUser.artStreamActive} />
        )}

        <EditionDetailsContainer step={step} />
        <GiftEditionContainer />
        {backgroundUrl && <BodyBackgroundImage url={backgroundUrl} />}
      </div>
    </WagmiProvider>
  );
};

const mapStateToProps = (state: Types.RootState): StateProps => {
  const editions = Object.values(state.editions.byId);
  const { isFetching } = state.playlists;

  // Retrieve newest edition background
  const newEdition = getFirstNewEdition(editions);
  let backgroundUrl: string | undefined = undefined;
  if (newEdition && state.artworks.byId[newEdition.artworkId]) {
    backgroundUrl = state.artworks.byId[newEdition.artworkId].thumbnailUrl;
  }

  return {
    isFetching,
    playlist: selectEditionsPlaylist(state),
    isFirstFetchDone: !state.editions.isInitial,
    currentUser: state.currentUser.user as Types.LoggedInUser,
    backgroundUrl,
    editions,
  };
};

const mapDispatchToProps = {
  fetchEditionsPlaylist,
};

export const EditionsGridContainer = withPlayerDispatcher(
  connect(mapStateToProps, mapDispatchToProps)(EditionsGrid),
);
